import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Megaphone, Check, Loader2, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const HERO = "https://images.unsplash.com/photo-1552174588-6733961c358e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwzfHxyYWRpbyUyMGhvc3QlMjBtaWNyb3Bob25lJTIwZGFyayUyMHN0dWRpb3xlbnwwfHx8fDE3ODc5Njk4OTh8MA&ixlib=rb-4.1.0&q=85";

const perks = ["High-fidelity audio ad", "Aired within gospel programming", "Reach devoted listeners", "Performance tracked by plays"];

export default function Advertise() {
  const [packages, setPackages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [business, setBusiness] = useState("");
  const [contact, setContact] = useState("");
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/ad-packages").then((res) => setPackages(res.data));
  }, []);

  const startCheckout = async () => {
    if (!user) { navigate("/login"); return; }
    if (!business.trim()) { toast.error("Business name is required"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: selected.lookup_key,
        origin_url: window.location.origin,
        business_name: business,
        contact_email: contact,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout");
      setLoading(false);
    }
  };

  return (
    <div>
      <section className="relative grain overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A1030] via-[#0A1030]/80 to-[#0A1030]/40" />
          <div className="absolute -top-20 right-10 w-96 h-96 rounded-full bg-[#5B7CFA]/15 blur-3xl" />
        </div>
        <div className="relative px-4 sm:px-6 lg:px-12 pt-16 pb-12 max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#5B7CFA]/15 border border-[#5B7CFA]/30 mb-6">
            <Megaphone className="w-3.5 h-3.5 text-[#AFC0FF]" />
            <span className="overline text-[#AFC0FF]">Advertise with us</span>
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05]">
            Put your message <span className="text-[#E6B34A]">on air</span>.
          </h1>
          <p className="text-zinc-300 text-lg leading-relaxed mt-5 max-w-xl">
            Buy a commercial spot, upload your audio ad, and reach thousands of faithful gospel radio listeners. Simple, transparent pricing.
          </p>
        </div>
      </section>

      <section className="px-4 sm:px-6 lg:px-12 py-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="packages-grid">
          {packages.map((p, i) => {
            const hl = !!p.badge;
            const best = p.badge === "Best value";
            return (
            <div
              key={p.lookup_key}
              className={`relative rounded-2xl border p-7 bg-[#141C42] fade-up flex flex-col ${hl ? (best ? "border-[#5B7CFA]/50 shadow-[0_0_40px_rgba(91,124,250,0.14)]" : "border-[#E6B34A]/50 shadow-[0_0_40px_rgba(230,179,74,0.12)]") : "border-white/5"}`}
              style={{ animationDelay: `${i * 70}ms` }}
              data-testid={`package-${p.lookup_key}`}
            >
              {p.badge && (
                <span className={`absolute -top-3 left-7 px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 ${best ? "bg-[#5B7CFA] text-white" : "bg-[#E6B34A] text-[#0A1030]"}`}>
                  <Sparkles className="w-3 h-3" /> {p.badge}
                </span>
              )}
              <p className={`overline ${best ? "text-[#AFC0FF]" : "text-[#E6B34A]"}`}>{best ? "Full-week bundle" : `${p.duration}-second spot`}</p>
              <h3 className="font-display font-extrabold text-2xl tracking-tight mt-2">{p.name}</h3>
              <div className="mt-4 flex items-end gap-1">
                <span className="font-display font-black text-4xl tracking-tighter">${p.price}</span>
                <span className="text-zinc-500 mb-1.5">one-time</span>
              </div>
              <ul className="mt-6 space-y-2.5 flex-1">
                <li className="flex items-center gap-2 text-sm text-zinc-300"><Check className="w-4 h-4 text-[#E6B34A]" /> {p.plays} guaranteed plays</li>
                <li className="flex items-center gap-2 text-sm text-zinc-300"><Check className="w-4 h-4 text-[#E6B34A]" /> {p.duration}s ad length</li>
                {p.note ? (
                  <li className="flex items-start gap-2 text-sm text-[#AFC0FF]"><Check className="w-4 h-4 mt-0.5 shrink-0" /> {p.note}</li>
                ) : perks.slice(0, 2).map((perk) => (
                  <li key={perk} className="flex items-center gap-2 text-sm text-zinc-300"><Check className="w-4 h-4 text-[#E6B34A]" /> {perk}</li>
                ))}
              </ul>
              <button
                onClick={() => { setSelected(p); setContact(user?.email || ""); }}
                data-testid={`buy-${p.lookup_key}`}
                className={`mt-7 w-full py-3 rounded-xl font-bold transition-colors ${hl && !best ? "bg-[#E6B34A] text-[#0A1030] hover:bg-[#CF9A34]" : best ? "bg-[#5B7CFA] text-white hover:bg-[#4665E0]" : "bg-white/5 text-white hover:bg-white/10"}`}
              >
                Buy this spot
              </button>
            </div>
          );})}
        </div>

        <p className="text-center text-sm text-zinc-500 mt-8">
          Already purchased? <button onClick={() => navigate("/my-spots")} className="text-[#E6B34A] font-semibold" data-testid="to-my-spots">Manage your spots →</button>
        </p>
      </section>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-[#141C42] border-white/10 text-white" data-testid="checkout-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-extrabold text-2xl tracking-tight">Buy {selected?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="rounded-xl bg-[#1E2A5A] p-4 flex items-center justify-between">
              <span className="text-zinc-400">{selected?.duration}s spot · {selected?.plays} plays</span>
              <span className="font-display font-bold text-xl">${selected?.price}</span>
            </div>
            <div>
              <label className="overline text-zinc-400">Business / brand name</label>
              <Input value={business} onChange={(e) => setBusiness(e.target.value)} placeholder="Grace Bakery" data-testid="checkout-business" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" />
            </div>
            <div>
              <label className="overline text-zinc-400">Contact email</label>
              <Input value={contact} onChange={(e) => setContact(e.target.value)} placeholder="you@business.com" data-testid="checkout-contact" className="mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]" />
            </div>
            <button onClick={startCheckout} disabled={loading} data-testid="checkout-pay" className="w-full py-3 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors disabled:opacity-60 flex items-center justify-center">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : `Pay $${selected?.price} securely`}
            </button>
            <p className="text-xs text-zinc-500 text-center">Powered by Stripe · Test card 4242 4242 4242 4242</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
